/**
 * UI primitives shared by every feature. Keep this file dependency-light; feature code composes these.
 */
import React, { useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { create } from "zustand";
import s from "./primitives.module.css";

// Button -------------------------------------------------------------------
export type ButtonVariant = "default" | "primary" | "ghost" | "outline" | "danger";
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  full?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "default", size = "md", full, loading, icon, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={[s.button, className].filter(Boolean).join(" ")}
      data-variant={variant}
      data-size={size}
      data-full={full ? "true" : undefined}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  active?: boolean;
}
export function IconButton({ label, active, className, children, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={[s.iconButton, className].filter(Boolean).join(" ")}
      data-active={active ? "true" : undefined}
      {...rest}
    >
      {children}
    </button>
  );
}

// Inputs -------------------------------------------------------------------
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={[s.input, className].filter(Boolean).join(" ")} {...rest} />;
});

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={[s.textarea, className].filter(Boolean).join(" ")} {...rest} />;
  },
);

export function Select({ className, children, ...rest }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={[s.select, className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </select>
  );
}

export function Field({ label, hint, error, children }: { label?: string; hint?: string; error?: string | null; children: ReactNode }) {
  return (
    <label className={s.field}>
      {label && <span className={s.fieldLabel}>{label}</span>}
      {children}
      {error ? <span className={s.fieldError}>{error}</span> : hint ? <span className={s.fieldHint}>{hint}</span> : null}
    </label>
  );
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (on: boolean) => void; label?: string }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={s.switch} data-on={on ? "true" : "false"} onClick={() => onChange(!on)} />;
}

export function Checkbox(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input type="checkbox" className={s.checkbox} {...props} />;
}

// Avatar -------------------------------------------------------------------
const AVATAR_COLORS = ["#f97316", "#ef4444", "#ec4899", "#8b5cf6", "#3b82f6", "#06b6d4", "#10b981", "#84cc16", "#eab308"];
export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: number;
  /** relationship bucket 1 closest .. 4 none; draws a tier ring */
  tier?: 1 | 2 | 3 | 4 | null;
  seed?: string;
  className?: string;
  title?: string;
}
export function Avatar({ name, src, size = 32, tier, seed, className, title }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
  const showImg = src && !failed;
  return (
    <span
      className={[s.avatar, className].filter(Boolean).join(" ")}
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.38), background: showImg ? undefined : avatarColor(seed ?? name) + "22", color: showImg ? undefined : avatarColor(seed ?? name) }}
      data-tier={tier && tier < 4 ? tier : undefined}
      title={title ?? name}
    >
      {showImg ? <img src={src!} alt={name} onError={() => setFailed(true)} /> : initials}
    </span>
  );
}

export function AvatarStack({ people, size = 28, max = 4 }: { people: { name: string; src?: string | null; seed?: string }[]; size?: number; max?: number }) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span className={s.avatarStack}>
      {shown.map((p, i) => (
        <Avatar key={i} name={p.name} src={p.src} size={size} seed={p.seed} />
      ))}
      {extra > 0 && (
        <span className={s.avatar} style={{ width: size, height: size, fontSize: 11 }}>
          +{extra}
        </span>
      )}
    </span>
  );
}

// Pills, cards, sections ---------------------------------------------------
export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info" | "purple";
export function Pill({ tone = "neutral", children, className, title }: { tone?: Tone; children: ReactNode; className?: string; title?: string }) {
  return (
    <span className={[s.pill, className].filter(Boolean).join(" ")} data-tone={tone} title={title}>
      {children}
    </span>
  );
}

export function Card({ children, interactive, onClick, className, style }: { children: ReactNode; interactive?: boolean; onClick?: () => void; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={[s.card, className].filter(Boolean).join(" ")} data-interactive={interactive ? "true" : undefined} onClick={onClick} style={style}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className={s.sectionTitle}>
      <span>{children}</span>
      {action}
    </div>
  );
}

export function ListRow({ children, selected, focused, onClick, className, ...rest }: React.HTMLAttributes<HTMLDivElement> & { selected?: boolean; focused?: boolean }) {
  return (
    <div className={[s.listRow, className].filter(Boolean).join(" ")} data-selected={selected ? "true" : undefined} data-focused={focused ? "true" : undefined} onClick={onClick} {...rest}>
      {children}
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      {icon}
      <div className={s.emptyTitle}>{title}</div>
      {children && <div>{children}</div>}
      {action}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span className={[s.spinner, className].filter(Boolean).join(" ")} aria-label="Loading" />;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={s.kbd}>{children}</kbd>;
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (id: T) => void }) {
  return (
    <div className={s.tabs} role="tablist">
      {tabs.map((t) => (
        <button key={t.id} role="tab" className={s.tab} data-active={t.id === value ? "true" : undefined} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// Modal --------------------------------------------------------------------
export function Modal({ open, onClose, title, children, footer, size }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode; size?: "md" | "lg" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} data-size={size} role="dialog" aria-modal="true">
        {title !== undefined && (
          <div className={s.modalHeader}>
            <span>{title}</span>
            <IconButton label="Close" onClick={onClose}>
              <X size={16} />
            </IconButton>
          </div>
        )}
        <div className={s.modalBody}>{children}</div>
        {footer && <div className={s.modalFooter}>{footer}</div>}
      </div>
    </div>
  );
}

// Menu (click to open, closes on outside click) ----------------------------
export interface MenuItemDef {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  shortcut?: string;
  onSelect?: () => void;
  divider?: boolean;
}
export function Menu({ trigger, items, align = "left" }: { trigger: ReactNode; items: MenuItemDef[]; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const toggle = () => {
    // Near the bottom of the window the list opens upward so it stays on screen (the sidebar account menu).
    const rect = ref.current?.getBoundingClientRect();
    setUp(!!rect && typeof window !== "undefined" && window.innerHeight - rect.bottom < 260);
    setOpen((o) => !o);
  };
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <span onClick={toggle} style={{ display: "inline-flex" }}>
        {trigger}
      </span>
      {open && (
        <div className={s.menu} role="menu" style={{ position: "absolute", ...(up ? { bottom: "calc(100% + 4px)" } : { top: "calc(100% + 4px)" }), [align]: 0 }}>
          {items.map((it) =>
            it.divider ? (
              <div key={it.id} className={s.menuDivider} />
            ) : (
              <button
                key={it.id}
                className={s.menuItem}
                data-danger={it.danger ? "true" : undefined}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onSelect?.();
                }}
              >
                {it.icon}
                <span className="u-grow">{it.label}</span>
                {it.shortcut && <Kbd>{it.shortcut}</Kbd>}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

// Toasts -------------------------------------------------------------------
export interface ToastAction {
  label: string;
  onClick: () => void;
}
interface Toast {
  id: number;
  message: string;
  tone: "neutral" | "success" | "danger";
  actions: ToastAction[];
}
interface ToastState {
  toasts: Toast[];
  push: (message: string, tone?: Toast["tone"], action?: ToastAction | ToastAction[]) => void;
  dismiss: (id: number) => void;
}
let toastSeq = 0;
export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone = "neutral", action) => {
    const id = ++toastSeq;
    const actions = action === undefined ? [] : Array.isArray(action) ? action : [action];
    set((st) => ({ toasts: [...st.toasts, { id, message, tone, actions }] }));
    // Toasts with actions stay a little longer so the user can reach them.
    setTimeout(() => set((st) => ({ toasts: st.toasts.filter((t) => t.id !== id) })), actions.length ? 8000 : 4500);
  },
  dismiss: (id) => set((st) => ({ toasts: st.toasts.filter((t) => t.id !== id) })),
}));

/** Shows a toast. `action` may be one action or several (rendered as separate buttons). */
export function toast(message: string, tone: Toast["tone"] = "neutral", action?: ToastAction | ToastAction[]) {
  useToasts.getState().push(message, tone, action);
}

export function Toaster() {
  const { toasts, dismiss } = useToasts();
  if (toasts.length === 0) return null;
  return (
    <div className={s.toasts}>
      {toasts.map((t) => (
        <div key={t.id} className={s.toast} data-tone={t.tone}>
          <span className="u-grow">{t.message}</span>
          {t.actions.map((a) => (
            <Button key={a.label} size="sm" variant="ghost" onClick={() => { a.onClick(); dismiss(t.id); }}>
              {a.label}
            </Button>
          ))}
          <IconButton label="Dismiss" onClick={() => dismiss(t.id)}>
            <X size={14} />
          </IconButton>
        </div>
      ))}
    </div>
  );
}

// Formatting helpers -------------------------------------------------------
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (now.getTime() - d.getTime()) / 1000;
  const abs = Math.abs(diff);
  const future = diff < 0;
  const fmt = (n: number, unit: string) => `${future ? "in " : ""}${n} ${unit}${n === 1 ? "" : "s"}${future ? "" : " ago"}`;
  if (abs < 60) return future ? "in a moment" : "just now";
  if (abs < 3600) return fmt(Math.floor(abs / 60), "minute");
  if (abs < 86400) return fmt(Math.floor(abs / 3600), "hour");
  if (abs < 86400 * 7) return fmt(Math.floor(abs / 86400), "day");
  if (abs < 86400 * 30) return fmt(Math.floor(abs / (86400 * 7)), "week");
  if (abs < 86400 * 365) return fmt(Math.floor(abs / (86400 * 30)), "month");
  return fmt(Math.floor(abs / (86400 * 365)), "year");
}

/** A birthday's next occurrence is a calendar day stored as UTC midnight; read in local time it lands a day early. */
export const BIRTHDAY_DATE: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" };

export function formatDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, opts);
}

export function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}
export const MOD_KEY = isMac() ? "Cmd" : "Ctrl";
